import { create } from 'zustand';
import {
  fetchMyAppsState,
  updateMyAppsEnabled,
  updateMyAppsPreferences
} from './myAppsApi';
import { moveEnabledAppId, reorderEnabledAppIds } from './myAppsReorder';
import type {
  MyAppsCatalogItem,
  MyAppsExternalApp,
  MyAppsState as MyAppsApiState
} from './myAppsTypes';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

const MAX_RECENT = 10;

interface MyAppsStoreState {
  status: LoadStatus;
  error: string | null;
  catalog: MyAppsCatalogItem[];
  externalApps: MyAppsExternalApp[];
  enabledAppIds: string[];
  pinnedToolIds: string[];
  recentToolIds: string[];
  /** True while a PUT is in flight (disables switches / reorder controls). */
  saving: boolean;
  load: () => Promise<void>;
  setEnabled: (enabledAppIds: string[]) => Promise<void>;
  toggleApp: (appId: string, enabled: boolean) => Promise<void>;
  reorderApps: (fromIndex: number, toIndex: number) => Promise<void>;
  moveApp: (appId: string, direction: -1 | 1) => Promise<void>;
  reorderPins: (fromIndex: number, toIndex: number) => Promise<void>;
  movePin: (toolId: string, direction: -1 | 1) => Promise<void>;
  togglePin: (toolId: string, pinned: boolean) => Promise<void>;
  recordRecent: (toolId: string) => Promise<void>;
  clearRecent: () => Promise<void>;
  reset: () => void;
}

function catalogWithEnabled(
  catalog: MyAppsCatalogItem[],
  enabledAppIds: string[]
): MyAppsCatalogItem[] {
  const enabled = new Set(enabledAppIds.map((id) => id.toLowerCase()));
  return catalog.map((item) => ({
    ...item,
    enabled: enabled.has(item.id.toLowerCase())
  }));
}

/** Serialize preference writes so rapid switch toggles / reorders cannot clobber each other. */
let writeChain: Promise<void> = Promise.resolve();

function stateFromApi(state: MyAppsApiState): Partial<MyAppsStoreState> {
  return {
    status: 'ready',
    catalog: state.catalog,
    externalApps: state.externalApps ?? [],
    enabledAppIds: state.enabledAppIds,
    pinnedToolIds: state.pinnedToolIds ?? [],
    recentToolIds: state.recentToolIds ?? [],
    error: null,
    saving: false
  };
}

async function persistEnabledIds(
  get: () => MyAppsStoreState,
  set: (partial: Partial<MyAppsStoreState>) => void,
  nextIds: string[]
) {
  set({
    enabledAppIds: nextIds,
    catalog: catalogWithEnabled(get().catalog, nextIds)
  });

  try {
    await get().setEnabled(nextIds);
  } catch (err) {
    try {
      await get().load();
    } catch {
      /* load error already stored */
    }
    throw err;
  }
}

function enqueueWrite(run: () => Promise<void>) {
  const next = writeChain.then(run, run);
  writeChain = next.then(
    () => undefined,
    () => undefined
  );
  return next;
}

export const useMyAppsStore = create<MyAppsStoreState>((set, get) => ({
  status: 'idle',
  error: null,
  catalog: [],
  externalApps: [],
  enabledAppIds: [],
  pinnedToolIds: [],
  recentToolIds: [],
  saving: false,
  async load() {
    if (get().status === 'loading') return;
    const silent = get().status === 'ready';
    if (!silent) set({ status: 'loading', error: null });
    try {
      const state = await fetchMyAppsState();
      set(stateFromApi(state));
    } catch (err) {
      if (silent) return;
      set({
        status: 'error',
        error: err instanceof Error ? err.message : 'Failed to load My Apps.',
        saving: false
      });
    }
  },
  async setEnabled(enabledAppIds) {
    set({ saving: true });
    try {
      const state = await updateMyAppsEnabled(enabledAppIds);
      set(stateFromApi(state));
    } catch (err) {
      set({ saving: false });
      throw err;
    }
  },
  async toggleApp(appId, enabled) {
    await enqueueWrite(async () => {
      const current = get().enabledAppIds;
      const nextIds = enabled
        ? current.includes(appId)
          ? [...current]
          : [...current, appId]
        : current.filter((id) => id.toLowerCase() !== appId.toLowerCase());

      await persistEnabledIds(get, set, nextIds);
    });
  },
  async reorderApps(fromIndex, toIndex) {
    await enqueueWrite(async () => {
      const nextIds = reorderEnabledAppIds(get().enabledAppIds, fromIndex, toIndex);
      if (nextIds.join('\0') === get().enabledAppIds.join('\0')) return;
      await persistEnabledIds(get, set, nextIds);
    });
  },
  async moveApp(appId, direction) {
    await enqueueWrite(async () => {
      const nextIds = moveEnabledAppId(get().enabledAppIds, appId, direction);
      if (nextIds.join('\0') === get().enabledAppIds.join('\0')) return;
      await persistEnabledIds(get, set, nextIds);
    });
  },
  async reorderPins(fromIndex, toIndex) {
    await enqueueWrite(async () => {
      const current = get().pinnedToolIds;
      const nextIds = reorderEnabledAppIds(current, fromIndex, toIndex);
      if (nextIds.join('\0') === current.join('\0')) return;
      set({ saving: true, pinnedToolIds: nextIds });
      try {
        const state = await updateMyAppsPreferences({ pinnedToolIds: nextIds });
        set(stateFromApi(state));
      } catch (err) {
        set({ saving: false, pinnedToolIds: current });
        throw err;
      }
    });
  },
  async movePin(toolId, direction) {
    await enqueueWrite(async () => {
      const current = get().pinnedToolIds;
      const nextIds = moveEnabledAppId(current, toolId, direction);
      if (nextIds.join('\0') === current.join('\0')) return;
      set({ saving: true, pinnedToolIds: nextIds });
      try {
        const state = await updateMyAppsPreferences({ pinnedToolIds: nextIds });
        set(stateFromApi(state));
      } catch (err) {
        set({ saving: false, pinnedToolIds: current });
        throw err;
      }
    });
  },
  async togglePin(toolId, pinned) {
    await enqueueWrite(async () => {
      const current = get().pinnedToolIds;
      const nextIds = pinned
        ? current.some((id) => id.toLowerCase() === toolId.toLowerCase())
          ? [...current]
          : [...current, toolId]
        : current.filter((id) => id.toLowerCase() !== toolId.toLowerCase());
      set({ saving: true, pinnedToolIds: nextIds });
      try {
        const state = await updateMyAppsPreferences({ pinnedToolIds: nextIds });
        set(stateFromApi(state));
      } catch (err) {
        set({ saving: false, pinnedToolIds: current });
        throw err;
      }
    });
  },
  async recordRecent(toolId) {
    await enqueueWrite(async () => {
      const current = get().recentToolIds;
      if (current[0]?.toLowerCase() === toolId.toLowerCase()) return;
      const nextIds = [
        toolId,
        ...current.filter((id) => id.toLowerCase() !== toolId.toLowerCase())
      ].slice(0, MAX_RECENT);
      set({ recentToolIds: nextIds });
      try {
        const state = await updateMyAppsPreferences({ recentToolIds: nextIds });
        set(stateFromApi(state));
      } catch (err) {
        set({ recentToolIds: current });
        throw err;
      }
    });
  },
  async clearRecent() {
    await enqueueWrite(async () => {
      const current = get().recentToolIds;
      set({ saving: true, recentToolIds: [] });
      try {
        const state = await updateMyAppsPreferences({ recentToolIds: [] });
        set(stateFromApi(state));
      } catch (err) {
        set({ saving: false, recentToolIds: current });
        throw err;
      }
    });
  },
  reset() {
    writeChain = Promise.resolve();
    set({
      status: 'idle',
      error: null,
      catalog: [],
      externalApps: [],
      enabledAppIds: [],
      pinnedToolIds: [],
      recentToolIds: [],
      saving: false
    });
  }
}));
